import log from './logger/log';

const openDatabase = (dbName, storeName, keyPath) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;

            if (!db.objectStoreNames.contains(storeName)) {
                db.createObjectStore(storeName, {keyPath});
            }
        };

        request.onsuccess = (event) => {
            resolve(event.target.result);
        };

        request.onerror = (event) => {
            log.error(`indexedDBService: Failed to open database. Database: ${dbName}`, event.target.error);
            reject(event.target.error);
        };
    });
};

const setDatabaseData = async (dbName, storeName, keyPath, data) => {
    const db = await openDatabase(dbName, storeName, keyPath);

    if (!db) return;

    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);

    store.add(data);
};

const putDatabaseItem = async (dbName, storeName, keyPath, data) => {
    const db = await openDatabase(dbName, storeName, keyPath);

    if (!db) return;

    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);

        const request = store.put(data);

        request.onsuccess = () => {
            log.debug(`IndexedDBService: item updated. Database: ${dbName}. Id: ${data?.data?.id}`);
            resolve(true);
        };

        request.onerror = (event) => {
            log.error(
                `IndexedDBService: failed to update item. Database: ${dbName}. Id: ${data?.data?.id}`,
                event.target.error
            );
            reject(event.target.error);
        };
    });
};

const getDatabaseData = async (dbName, storeName, keyPath) => {
    try {
        const db = await openDatabase(dbName, storeName, keyPath);

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);

            const request = store.getAll();

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = (event) => {
                reject(event.target.error);
            };
        });
    } catch (e) {
        log.error(`IndexedDBService: Failed to receive data. Database: ${dbName}`, e);

        return [];
    }
};

const getDatabaseItemById = async (dbName, storeName, keyPath, id) => {
    try {
        const db = await openDatabase(dbName, storeName, keyPath);

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);

            const request = store.get(id);

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = (event) => {
                reject(event.target.error);
            };
        });
    } catch (e) {
        log.error(`IndexedDBService: Failed to receive item. Database: ${dbName}. Id: ${id}`, e);
        throw e;
    }
};

const deleteDatabaseItemById = async (dbName, storeName, keyPath, id) => {
    try {
        const db = await openDatabase(dbName, storeName, keyPath);

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);

            const request = store.delete(id);

            request.onsuccess = () => {
                log.debug(`IndexedDBService: Successfully deleted item. Database: ${dbName}. Id: ${id}`);
                resolve(true);
            };

            request.onerror = (event) => {
                reject(event.target.error);
            };
        });
    } catch (e) {
        log.error(`IndexedDBService: Failed to delete item. Database: ${dbName}. Id: ${id}`, e);
        throw e;
    }
};

const clearDatabase = (dbName) => {
    return new Promise((resolve) => {
        const request = indexedDB.deleteDatabase(dbName);

        request.onsuccess = () => {
            log.debug(`indexedDBService: Database cleared successfully. Database: ${dbName}`);
            resolve();
        };

        request.onerror = (event) => {
            log.debug(`indexedDBService: Failed to clear database. Database: ${dbName}`, event.target.error);
            resolve();
        };
    });
};

const clearDatabases = async () => {
    const databaseNames = [];

    await Promise.all(databaseNames.map(clearDatabase));
};

export default {
    setDatabaseData,
    getDatabaseData,
    getDatabaseItemById,
    putDatabaseItem,
    deleteDatabaseItemById,
    clearDatabases,
};
